import React, {Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useState} from 'react';
import {Checkbox, Col, Form, Input, InputNumber, message, Row} from "antd";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {
  fetchPlaneTypes,
} from "../../../../../utils/helpers/selectDataFetchers/fetchPlaneTypes";
import {
  fetchAirports,
} from "../../../../../utils/helpers/selectDataFetchers/fetchAirports";
import styled from "styled-components";
import {CustomDatePicker} from '../../../../../components/UI/AntCustom/CustomDatePicker';
import {UploadOutlined} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import {UploadFile} from "antd/es/upload";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {clearCreatePlane, createPlane} from "../../../../../store/reducers/fleetReducer/fleetReducer";
import {renderSuccessModal} from "../../../../../components/UI/StatusModals/renderSuccessModal";
import {Dayjs} from "dayjs";
import dayjs from "dayjs";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {FileAcceptTypes} from "../../../../../utils/constans/files/fileAcceptTypes";
import {CustomUpload} from "../../../../../components/UI/AntCustom/CustomUpload";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {validateFileSize, getFileSizeErrorMessage} from "../../../../../utils/helpers/fileHelpers/validateFileSize";
import {RcFile} from "antd/es/upload";
import {
  fetchCurrencies,
} from "../../../../../utils/helpers/selectDataFetchers/fetchCurrencies";
import {
  FlyHourPriceContext,
} from './flyHourPriceCalculator/context/FlyHourPriceContextProvider';
import {PlaneCalculatorSettingEntity} from "../../../../../proto/generated/api_entities_pb";
import {FormInstance} from "antd/es";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import {useIsMobile} from "../../../../../hooks/useWindowSize";
import {WarningShield} from "../../../../../components/UI/WarningShield/WarningShield";
import {convertToKm, getUnitLabel} from "../../../../../utils/helpers/distanceConverter/distanceConverter";
import {DistanceUnit} from "../../../../../utils/constans/statuses/distanceUnits";


const FormItemsWrapper = styled.div`
  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-upload-list-item-name {
    cursor: pointer;
  }
`;

const FormButtons = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 10px;

  button {
    flex-grow: 1;
  }
`;


const PlaneCreateFormMessages = defineMessages({
  success: {
    id: "planeCreateForm.title",
    defaultMessage: "The new aircraft has been successfully added for administrator's moderation.",
  },
  createPlane: {
    id: "planeCreateForm.createPlane",
    defaultMessage: "Create plane",
  },
  openCalculator: {
    id: "planeCreateForm.openCalculator",
    defaultMessage: "Calculate your flight cost",
  },
  openCalculatorMobile: {
    id: "planeCreateForm.openCalculatorMobile",
    defaultMessage: "Flight hour cost calculator is available only in web-version",
  },
  trialPlaneLimitReached: {
    id: "planeCreateForm.trialPlaneLimitReached",
    defaultMessage: "Trial mode allows adding up to 7 aircraft. Please upgrade your subscription to add more.",
  },
  aircraftSlotLimitReached: {
    id: "planeCreateForm.aircraftSlotLimitReached",
    defaultMessage: "You have reached your aircraft slot limit. Ask your company administrator to buy more slots in Subscription.",
  },
})

interface ICreatePlaneFormData {
  planeTypeId: number;
  baseAirportId: number;
  regNumber: string;
  yearOfMake: Dayjs;
  refurbishedDate?: Dayjs;
  maxPax: number;
  maxDistance: number;
  animals: boolean;
  cargo: boolean;
  smoking: boolean;
  ambulance: boolean;
  wifi: boolean;
  ovenAboard: boolean;
  description?: string;
  flyHourPrice: number;
  flyHourCurrencyId: number;
  settingEntities: PlaneCalculatorSettingEntity[];
}

interface IPlaneCreateForm {
  form: FormInstance;
  closeDrawer: () => void;
  imageFiles: UploadFile[];
  setIsCalculatorOpen: Dispatch<SetStateAction<boolean>>;
}

export const PlaneCreateForm: FC<IPlaneCreateForm> = ({form, closeDrawer, imageFiles, setIsCalculatorOpen}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const {isLoading, isSuccess, errorMessage} = useAppSelector(state => state.fleet.create);
  const planes = useAppSelector(state => state.fleet.itemsObject.items);
  const tariff = useAppSelector(state => state.tariff.oneItemObject.item);
  const [insuranceFile, setInsuranceFile] = useState<UploadFile | undefined>();
  const [certificateFile, setCertificateFile] = useState<UploadFile | undefined>();
  const userDistanceUnit = useAppSelector(state => state.user.oneItemObject.item.distanceUnit) || DistanceUnit.NAUTICAL_MILE;
  const [etopsFile, setEtopsFile] = useState<UploadFile | undefined>();
  const {savedFlyHourPrice, savedCurrency, savedSettingEntities} = useContext(FlyHourPriceContext);

  useEffect(() => {
    return () => {
      dispatch(clearCreatePlane());
    }
  }, [])

  useEffect(() => {
    form.setFieldsValue({
      'flyHourPrice': savedFlyHourPrice,
      'flyHourCurrencyId': savedCurrency?.id
    })
  }, [savedFlyHourPrice, savedCurrency, savedSettingEntities])

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess)
        renderSuccessModal(intl.formatMessage(PlaneCreateFormMessages.success), closeDrawer);
      if (errorMessage)
        message.error(errorMessage);
    }
  }, [isSuccess, errorMessage, isLoading])

  // Check if trial limit is reached
  const isTrialLimitReached = useMemo(() => {
    const activeTrialPackage = tariff?.companyTariffPackages?.find(pkg => pkg.active && pkg.isTrial);
    if (!activeTrialPackage) return false;
    return planes.length >= 7;
  }, [tariff, planes.length]);

  // Check if paid subscription aircraft slot limit is reached (billed >= subscribed)
  const isAircraftSlotLimitReached = useMemo(() => {
    const activePaidPackage = tariff?.companyTariffPackages?.find(
      pkg => pkg.active && !pkg.isTrial && (pkg.subscribedAircraftCount ?? 0) > 0
    );
    if (!activePaidPackage) return false;
    const subscribed = activePaidPackage.subscribedAircraftCount ?? 0;
    const billed = activePaidPackage.billedAircraftCount ?? 0;
    return billed >= subscribed;
  }, [tariff])

  const onFinish = (values: ICreatePlaneFormData) => {
    // Check trial limit
    if (isTrialLimitReached) {
      message.error(intl.formatMessage(PlaneCreateFormMessages.trialPlaneLimitReached));
      return;
    }
    // Check aircraft slot limit
    if (isAircraftSlotLimitReached) {
      message.error(intl.formatMessage(PlaneCreateFormMessages.aircraftSlotLimitReached));
      return;
    }

    if (!insuranceFile) {
      message.error(intl.formatMessage(CommonFormMessages.pdfInsuranceNotLoaded))
      return;
    }

    if (!certificateFile) {
      message.error(intl.formatMessage(CommonFormMessages.pdfRegistrationCertificateNotLoaded))
      return;
    }

    dispatch(createPlane({
      planeTypeId: values.planeTypeId!,
      baseAirportId: values.baseAirportId!,
      registrationNumber: values.regNumber!,
      manufactureDate: values.yearOfMake.toDate(),
      renovationDate: values.refurbishedDate?.toDate(),
      maxPassengers: values.maxPax!,
      maxDistance: convertToKm(values.maxDistance!, userDistanceUnit),
      isAnimals: values.animals,
      isCargo: values.cargo,
      isSmoking: values.smoking,
      isAmbulance: values.ambulance,
      isWifi: values.wifi,
      isOvenAboard: values.ovenAboard,
      insuranceFile: insuranceFile,
      certificateFile: certificateFile,
      etopsFile: etopsFile,
      imageFiles: imageFiles,
      description: values.description,
      flyHourPrice: values.flyHourPrice,
      flyHourCurrencyId: values.flyHourCurrencyId,
      settingEntities: savedSettingEntities,
    }))
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const handleInsuranceFileUpload = (file: RcFile) => {
    if (!validateFileSize(file)) {
      message.error(getFileSizeErrorMessage(intl));
      return false;
    }
    setInsuranceFile(file);
    return false;
  }

  const handleCertificateFileUpload = (file: RcFile) => {
    if (!validateFileSize(file)) {
      message.error(getFileSizeErrorMessage(intl));
      return false;
    }
    setCertificateFile(file);
    return false;
  }

  const handleEtopsFileUpload = (file: RcFile) => {
    if (!validateFileSize(file)) {
      message.error(getFileSizeErrorMessage(intl));
      return false;
    }
    setEtopsFile(file);
    return false;
  }

  const getManufactureYear = () => {
    const manufactureDate = form.getFieldValue('yearOfMake');
    return manufactureDate ? manufactureDate.year() : null;
  }

  const getRefurbishedYear = () => {
    const refurbishedDate = form.getFieldValue('refurbishedDate');
    return refurbishedDate ? refurbishedDate.year() : null;
  }

  const initialValues: Partial<ICreatePlaneFormData> = {
    planeTypeId: undefined,
    baseAirportId: undefined,
    regNumber: undefined,
    yearOfMake: undefined,
    refurbishedDate: undefined,
    maxPax: undefined,
    maxDistance: undefined,
    animals: false,
    cargo: false,
    smoking: false,
    ambulance: false,
    wifi: false,
    ovenAboard: false,
    description: undefined,
    flyHourPrice: undefined,
    flyHourCurrencyId: undefined,
    settingEntities: [],
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout={'vertical'}
      initialValues={initialValues}
    >
      <FormItemsWrapper>
        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.planeLabel)}
            name={'planeTypeId'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <DebounceSelect
              placeholder={intl.formatMessage(CommonFormMessages.planePlaceholder)}
              showSearch={true}
              optionFilterProp="children"
              fetchOptions={fetchPlaneTypes}
            />
          </CustomFieldInput>
        </Col>

        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.baseAirportLabel)}
            name={'baseAirportId'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <DebounceSelect
              placeholder={intl.formatMessage(CommonFormMessages.baseAirportPlaceholder)}
              showSearch={true}
              optionFilterProp="children"
              fetchOptions={fetchAirports}
            />
          </CustomFieldInput>
        </Col>

        <Row gutter={8}>
          <Col span={8}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.registrationNumberLabel)}
              name={'regNumber'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <Input placeholder={intl.formatMessage(CommonFormMessages.registrationNumberPlaceholder)}
                     style={{width: "100%"}}/>
            </CustomFieldInput>
          </Col>

          <Col span={8}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.manufactureDateLabel)}
              name={'yearOfMake'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <CustomDatePicker picker={"year"} placeholder={"2010"} style={{width: "100%"}}
                                inputReadOnly={true} placement={"bottomLeft"}
                                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                                disabledDate={(current) => {
                                  if (!current) return false;
                                  const currentYear = current.year();
                                  const currentYearLimit = dayjs().year();
                                  const refurbishedYear = getRefurbishedYear();
                                  
                                  // Нельзя выбрать год больше текущего
                                  if (currentYear > currentYearLimit) return true;
                                  
                                  // Нельзя выбрать год изготовления больше года ремонта
                                  if (refurbishedYear && currentYear > refurbishedYear) return true;
                                  
                                  return false;
                                }}
              />
            </CustomFieldInput>
          </Col>

          <Col span={8}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.refurbishDateLabel)}
              name={'refurbishedDate'}
            >
              <CustomDatePicker picker={"year"} placeholder={"2021"} style={{width: "100%"}}
                                inputReadOnly={true} placement={"bottomLeft"}
                                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                                disabledDate={(current) => {
                                  if (!current) return false;
                                  const currentYear = current.year();
                                  const currentYearLimit = dayjs().year();
                                  const manufactureYear = getManufactureYear();
                                  
                                  // Нельзя выбрать год больше текущего
                                  if (currentYear > currentYearLimit) return true;
                                  
                                  // Нельзя выбрать год ремонта меньше года изготовления
                                  if (manufactureYear && currentYear < manufactureYear) return true;
                                  
                                  return false;
                                }}
              />
            </CustomFieldInput>
          </Col>
        </Row>

        <Row gutter={8}>
          <Col span={12}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.paxLabel)}
              name={'maxPax'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <CustomInputNumber controls={true} placeholder={"12"} style={{width: "100%"}}/>
            </CustomFieldInput>
          </Col>

          <Col span={12}>
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.distanceLabel).replace(', km', '')}, ${getUnitLabel(userDistanceUnit)}`}
              name={'maxDistance'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <CustomInputNumber controls={true} placeholder={getUnitLabel(userDistanceUnit) === 'nm' ? '1242' : '2300'} style={{width: "100%"}}/>
            </CustomFieldInput>
          </Col>
        </Row>

        <Col flex={1}>
          <Form.Item
            label={intl.formatMessage(CommonFormMessages.planeInsuranceFile)}
            required={true}
            rules={[{
              validator: async () => {
                if (!insuranceFile) throw new Error(intl.formatMessage(CommonFormMessages.ruleRequired));
              }
            }]}
          >
            <CustomUpload
              accept={FileAcceptTypes.DOCUMENT}
              beforeUpload={handleInsuranceFileUpload}
              fileList={insuranceFile ? [insuranceFile] : []}
              maxCount={1}
              onRemove={() => setInsuranceFile(undefined)}
            >
              <CustomButton icon={<UploadOutlined/>} style={{background: "transparent"}}
                            type={"dashed"}>{intl.formatMessage(CommonButtonMessages.loadPdf)}</CustomButton>
            </CustomUpload>
          </Form.Item>
        </Col>

        <Col flex={1} style={{marginBottom: 24}}>
          <Form.Item
            label={intl.formatMessage(CommonFormMessages.planeRegistrationCertificateFile)}
            required={true}
            rules={[{
              validator: async () => {
                if (!certificateFile) throw new Error(intl.formatMessage(CommonFormMessages.ruleRequired));
              }
            }]}
          >
            <CustomUpload
              accept={FileAcceptTypes.DOCUMENT}
              beforeUpload={handleCertificateFileUpload}
              fileList={certificateFile ? [certificateFile] : []}
              maxCount={1}
              onRemove={() => setCertificateFile(undefined)}
            >
              <CustomButton icon={<UploadOutlined/>} style={{background: "transparent"}}
                            type={"dashed"}>{intl.formatMessage(CommonButtonMessages.loadPdf)}</CustomButton>
            </CustomUpload>
          </Form.Item>
        </Col>

        <Col flex={1} style={{marginBottom: 24}}>
          <Form.Item
            label={intl.formatMessage(CommonFormMessages.planeEtopsFile)}
          >
            <CustomUpload
              accept={FileAcceptTypes.DOCUMENT}
              beforeUpload={handleEtopsFileUpload}
              fileList={etopsFile ? [etopsFile] : []}
              maxCount={1}
              onRemove={() => setEtopsFile(undefined)}
            >
              <CustomButton icon={<UploadOutlined/>} style={{background: "transparent"}}
                            type={"dashed"}>{intl.formatMessage(CommonButtonMessages.loadPdf)}</CustomButton>
            </CustomUpload>
          </Form.Item>
        </Col>


        <Row style={{marginBottom: 16}}>
          <Col span={12}>
            <CustomFieldInput
              name={'animals'}
              valuePropName={'checked'}
            >
              <Checkbox>{intl.formatMessage(CommonFormMessages.animalsCheckbox)}</Checkbox>
            </CustomFieldInput>
          </Col>

          <Col span={12}>
            <CustomFieldInput
              name={'cargo'}
              valuePropName={'checked'}
            >
              <Checkbox>{intl.formatMessage(CommonFormMessages.cargoCheckbox)}</Checkbox>
            </CustomFieldInput>
          </Col>

          <Col span={12}>
            <CustomFieldInput
              name={'smoking'}
              valuePropName={'checked'}
            >
              <Checkbox>{intl.formatMessage(CommonFormMessages.smokingCheckbox)}</Checkbox>
            </CustomFieldInput>
          </Col>

          <Col span={12}>
            <CustomFieldInput
              name={'ambulance'}
              valuePropName={'checked'}
            >
              <Checkbox>{intl.formatMessage(CommonFormMessages.ambulanceCheckbox)}</Checkbox>
            </CustomFieldInput>
          </Col>

          <Col span={12}>
            <CustomFieldInput
              name={'wifi'}
              valuePropName={'checked'}
            >
              <Checkbox>{intl.formatMessage(CommonFormMessages.wifiCheckbox)}</Checkbox>
            </CustomFieldInput>
          </Col>

          <Col span={12}>
            <CustomFieldInput
              name={'ovenAboard'}
              valuePropName={'checked'}
            >
              <Checkbox>{intl.formatMessage(CommonFormMessages.ovenAboardCheckbox)}</Checkbox>
            </CustomFieldInput>
          </Col>
        </Row>

        <Col span={24}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.planeDescriptionLabel)}
            name={'description'}
          >
            <TextArea autoSize={true} placeholder={intl.formatMessage(CommonFormMessages.planeDescriptionPlaceholder)}
                      style={{width: "100%"}}/>
          </CustomFieldInput>
        </Col>
      </FormItemsWrapper>

      <Row gutter={[8, 8]}>
        <Col span={16}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.flyHourPriceLabel)}
            name={'flyHourPrice'}
            rules={[
              {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
              {min: 0, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinimumZero)},
              {max: 1000000000, type: "number", message: intl.formatMessage(CommonFormMessages.ruleHugeNumber)}
            ]}
            style={{marginBottom: 0}}
          >
            <CustomInputNumber controls={false}
                               placeholder={intl.formatMessage(CommonFormMessages.flyHourPricePlaceholder)}
                               style={{width: '100%'}}/>
          </CustomFieldInput>
        </Col>
        <Col span={8}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.currencyLabel)}
            name={'flyHourCurrencyId'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            style={{marginBottom: 0}}
          >
            <DebounceSelect
              defaultOptionsFetchCallback={fetchCurrencies}
              placeholder={intl.formatMessage(CommonFormMessages.currencyPlaceholder)}
            />
          </CustomFieldInput>
        </Col>

        <Col span={24}>
          {isMobile
            ? <WarningShield warningText={intl.formatMessage(PlaneCreateFormMessages.openCalculatorMobile)}
                             style={{marginBottom: 32}}/>
            : <CustomButton type={'primary'} ghost={true} style={{width: '100%', marginBottom: 32}}
                            onClick={() => setIsCalculatorOpen(true)}>
              {intl.formatMessage(PlaneCreateFormMessages.openCalculator)}
            </CustomButton>
          }
        </Col>
      </Row>

      <FormButtons>
        <CustomButton danger type={'link'}
                      onClick={closeDrawer}>{intl.formatMessage(CommonButtonMessages.cancel)}</CustomButton>
        <CustomButton type={'primary'}
                      htmlType={'submit'}
                      disabled={isTrialLimitReached || isAircraftSlotLimitReached}>{intl.formatMessage(PlaneCreateFormMessages.createPlane)}</CustomButton>
      </FormButtons>
    </Form>
  );
};
